import { useQuery } from '@tanstack/react-query';

import { baseAPI } from '~shared/api/base-api';
import { QUERY_KEYS } from '~shared/api/query-keys';

import {
  HourlyTuitionDiscountType,
  HourlyTuitionPostingStatus,
} from './hourly-tuition.constants';

export interface SessionDetail {
  day: string;
  class: string;
  minutes: number;
}

export interface ApplicableRange {
  minMinute: number;
  maxMinute: number;
  flatRate: number;
}

export interface WeeklyBreakdown {
  weekStart: string;
  weekEnd: string;
  minutes: number;
  hourlyRate: number;
  billingAmount: number;
  applicableRange: ApplicableRange | null;
  errorMessage: string | null;
  isProrated?: boolean;
  days?: Array<string>;
  prorationReason?: string | null;
  isPosted?: boolean;
  postedInvoiceId?: string | null;
  postedInvoiceStatus?: string | null;
  postedDateRange?: { fromDate: string; toDate: string } | null;
}

export interface Enrollment {
  enrollmentId: string;
  enrollmentTitle: string;
  billingRuleName: string;
  status: string;
  sessionDetails: Array<SessionDetail>;
  totalMinutes: number;
  hourlyRate: number;
  totalTuition: number;
  applicableRange: ApplicableRange | null;
  errorMessage: string | null;
  weeklyBreakdown?: Array<WeeklyBreakdown>;
  discountApplied: number;
  finalAmount: number;
  discountType?: HourlyTuitionDiscountType;
  discountValue?: number;
}

export interface StudentBillingData {
  studentId: string;
  studentName: string;
  parentId: string;
  enrollments: Array<Enrollment>;
  totalMinutes: number;
  totalTuition: number;
  discountApplied: number;
  finalAmount: number;
  discountType?: HourlyTuitionDiscountType;
  /** Staff/manual override only. Never auto multi-student discount. */
  discountValue?: number;
  /** V2 auto multi-student discount already baked into finalAmount. */
  multiStudentDiscount?: number;
  isDraft?: boolean; // Flag indicating if this student has a draft
  draftId?: string; // ID of the draft if it exists
  postingStatus?: HourlyTuitionPostingStatus;
  postedInvoiceId?: string | null;
  postedInvoiceStatus?: string | null;
  postedWeekCount?: number;
  unpostedWeekCount?: number;
}

export interface DateRange {
  fromDate: string;
  toDate: string;
}

export interface SessionBillingSummary {
  totalStudents: number;
  totalEnrollments: number;
  totalBillingAmount: number;
  totalMinutes: number;
  dateRange: DateRange;
}

export interface StudentInfo {
  studentId: string;
  studentName: string;
  invoiceId: string | null;
  invoiceStatus: string | null;
}

export interface ProcessingRecord {
  processedAt: string;
  processedAtFormatted: string;
  postedFromDate?: string;
  postedToDate?: string;
  studentCount: number;
  postingCount?: number;
  students: Array<StudentInfo>;
}

export interface SessionBillingBreakdownResponse {
  success: boolean;
  data: Array<StudentBillingData>;
  summary: SessionBillingSummary;
  warningMessage?: string | null;
  processingHistory?: Array<ProcessingRecord> | null;
}

export interface GetSessionBillingBreakdownParams {
  sessionId: string;
  fromDate: string;
  toDate: string;
}

export const getSessionBillingBreakdownAPI = async (
  params: GetSessionBillingBreakdownParams,
): Promise<SessionBillingBreakdownResponse> => {
  const { sessionId, fromDate, toDate } = params;

  const response = await baseAPI.get<SessionBillingBreakdownResponse>(
    `hourly-tuition-billing/session-billing-breakdown/${sessionId}`,
    {
      params: {
        fromDate,
        toDate,
      },
    },
  );

  return response.data;
};

export const useGetSessionBillingBreakdown = (
  params: GetSessionBillingBreakdownParams,
) => {
  return useQuery({
    queryKey: QUERY_KEYS.SESSION_BILLING_BREAKDOWN(params),
    queryFn: () => getSessionBillingBreakdownAPI(params),
    enabled: false, // Disable automatic fetching - only fetch when refetch() is called
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};
